import React, { useState, useCallback } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDocumentsInfo, generateDocumentUrl, DocumentInfo } from '../thoughtRenderer/toolResults/documentHelpers';
import { getDocumentStyleClasses } from '../../../utils/documentStyles';
import FileIconSVG from '@/taskpane/shared/components/FileIconSVG';

interface DocumentBadgeProps {
  documentId: string;
  documentName?: string;
  documentType?: string;
  documentUrl?: string;
  page?: number;
  className?: string;
  variant?: 'default' | 'compact';
  onViewDocument?: (url: string, name: string) => void;
}

/**
 * Componente que renderiza un badge clickeable para un documento referenciado
 * Similar al DocumentBadge de la guía pero adaptado para Word Plugin
 */
export function DocumentBadge({ 
  documentId, 
  documentName, 
  documentType, 
  documentUrl,
  page,
  className,
  variant = 'default',
  onViewDocument
}: DocumentBadgeProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadedDocument, setLoadedDocument] = useState<DocumentInfo | null>(null);

  // Detectar la extensión del archivo
  const getFileExtension = (name?: string, url?: string): string => {
    // 1. Intentar desde el nombre
    if (name && name.includes('.')) {
      const parts = name.split('.');
      const ext = parts[parts.length - 1].toLowerCase();
      if (ext) return ext;
    }
    
    // 2. Intentar desde la URL (antes de los parámetros de query)
    if (url) {
      try {
        const urlPath = url.split('?')[0];
        const parts = urlPath.split('.');
        const ext = parts[parts.length - 1].toLowerCase();
        if (ext && ext.length <= 5) return ext; // Validar que parece una extensión
      } catch (e) {
        // Ignorar errores de parseo de URL
      }
    }
    
    return '';
  };

  const getFileType = (name?: string, url?: string): string => {
    const ext = getFileExtension(name, url || documentUrl);
    if (!ext) return documentType || 'pdf';
    return ext;
  };

  const handleClick = useCallback(async () => {
    const getUrlWithPage = (url: string, name?: string) => {
      const ext = getFileExtension(name || documentName, url);
      
      // For PDFs, append page number
      if (ext === 'pdf') {
        const pageNum = page || 1;
        if (!url.includes('#page=')) {
          return `${url}#page=${pageNum}`;
        }
      }
      
      return url;
    };

    // Si ya tenemos la URL, abrir directamente
    if (documentUrl && onViewDocument) {
      onViewDocument(getUrlWithPage(documentUrl, documentName), documentName || `Document ${documentId.substring(0, 8)}...`);
      return;
    }

    // Si ya cargamos el documento antes, usar esos datos
    if (loadedDocument?.pdf_url && onViewDocument) {
      onViewDocument(getUrlWithPage(loadedDocument.pdf_url, loadedDocument.name), loadedDocument.name);
      return;
    }

    // Cargar el documento desde la BD
    setIsLoading(true);
    try {
      const docs = await getDocumentsInfo([documentId]);
      if (docs.length > 0) {
        const doc = docs[0];
        let finalUrl = doc.pdf_url;

        // Si tenemos source_path pero no pdf_url, generar la URL
        if (!finalUrl && doc.source_path) {
          finalUrl = await generateDocumentUrl(doc.source_path);
        }

        if (finalUrl) {
          const docInfo: DocumentInfo = {
            ...doc,
            pdf_url: finalUrl
          };
          setLoadedDocument(docInfo);
          
          const urlToOpen = getUrlWithPage(finalUrl, doc.name || documentName);
          
          if (onViewDocument) {
            onViewDocument(urlToOpen, doc.name || documentName || `Document ${documentId.substring(0, 8)}...`);
          } else {
            // Fallback: abrir en nueva pestaña
            window.open(urlToOpen, '_blank');
          }
        } else {
          console.error('No se pudo obtener URL del documento');
        }
      } else {
        console.error('Documento no encontrado:', documentId);
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, documentUrl, documentName, documentType, loadedDocument, onViewDocument, page]);

  const displayName = documentName || loadedDocument?.name || `Document ${documentId.substring(0, 8)}...`;

  // Todas las pills de documento usan el mismo estilo gris claro
  const styles = getDocumentStyleClasses('file');

  const isCompact = variant === 'compact';

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border transition-all duration-200 cursor-pointer disabled:opacity-50 select-none whitespace-nowrap group",
        // Reemplazar estilos rojos hardcoded con estilos unificados
        // "bg-red-50/60 hover:bg-red-50",
        // "text-red-700 border-red-200/50",
        styles.bg,
        styles.hover,
        styles.text,
        styles.border,
        isCompact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]",
        className
      )}
      title="Click to view document"
    >
      {isLoading ? (
        <Loader2 size={isCompact ? 10 : 11} className={cn("animate-spin", styles.text)} />
      ) : (
        <FileIconSVG 
          filename={displayName} 
          extension={getFileType(displayName, loadedDocument?.pdf_url || documentUrl)}
          size={isCompact ? 11 : 12} 
          className={cn("flex-shrink-0 opacity-70", styles.text)} 
        />
      )}
      <span className={cn("truncate font-normal flex items-center gap-1", isCompact ? "max-w-[100px]" : "max-w-[150px]")}>
        <span className="truncate">{displayName}</span>
        {page && <span className="opacity-60 text-[0.9em] whitespace-nowrap">p. {page}</span>}
      </span>
      <ExternalLink 
        size={isCompact ? 8 : 9} 
        className="opacity-30 group-hover:opacity-50 flex-shrink-0 transition-opacity" 
      />
    </button>
  );
}
